// src/pages/gestion/Marches.jsx
import React, { useState, useEffect, useRef } from "react";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { useAuth } from "../../hooks/useAuth";
import { Link, useNavigate } from "react-router-dom";
import { 
  Card, 
  Table, 
  Row, 
  Col, 
  Form, 
  InputGroup, 
  Button, 
  Pagination, 
  Badge, 
  Modal, 
  Toast,
  ToastContainer,
  Spinner,
  FloatingLabel
} from "react-bootstrap";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  Search,
  PlusLg,
  Eye,
  Pencil,
  Trash,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Check,
  X,
  ExclamationCircle,
  Building,
  GeoAlt,
  InfoCircle,
  Shop,
  Cloud,
  FileExcel,
  People,
  CashStack,
  CheckSquare,
  Square
} from "react-bootstrap-icons";
import { API_BASE_URL } from "../../constants/api_constants";

const MARCHES_ENDPOINT = `${API_BASE_URL}/marches`;
const LOCALITES_ENDPOINT = `${API_BASE_URL}/localites`;
const TAXPAYERS_ENDPOINT = `${API_BASE_URL}/taxpayers`;

export default function Marches() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userRole = user?.role?.name || 'agent';
  const defaultCurrency = sessionStorage.getItem("default_currency") || 'XOF';
  
  // États principaux
  const [marches, setMarches] = useState([]);
  const [quartiers, setQuartiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingQuartiers, setLoadingQuartiers] = useState(false);
  const [error, setError] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('nom_marche');
  const [sortDirection, setSortDirection] = useState('asc');
  const [pagination, setPagination] = useState({
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
    from: 0,
    to: 0
  });
  
  // États pour les modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  
  // États pour les données des modals
  const [selectedMarche, setSelectedMarche] = useState(null);
  const [marcheToDelete, setMarcheToDelete] = useState(null);
  const [newMarche, setNewMarche] = useState({
    code_marche: "",
    nom_marche: "",
    id_quartier: "",
    superficie: "",
    etat: "Ouvert",
    observations: ""
  });
  const [editedMarche, setEditedMarche] = useState(null);
  
  // États pour la recherche de quartier
  const [quartierSearchTerm, setQuartierSearchTerm] = useState('');
  const [showQuartierDropdown, setShowQuartierDropdown] = useState(false);
  const [selectedQuartierName, setSelectedQuartierName] = useState('');
  const quartierSearchRef = useRef(null);
  
  // États pour l'édition
  const [editQuartierSearchTerm, setEditQuartierSearchTerm] = useState('');
  const [showEditQuartierDropdown, setShowEditQuartierDropdown] = useState(false);
  const [editSelectedQuartierName, setEditSelectedQuartierName] = useState('');
  const editQuartierSearchRef = useRef(null);
  
  // États pour les chargements
  const [loadingAdd, setLoadingAdd] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  
  // États pour l'export
  const [exportLoading, setExportLoading] = useState(false);
  const [exportMarcheLoading, setExportMarcheLoading] = useState(null);
  const [selectedMarchesForExport, setSelectedMarchesForExport] = useState([]);
  const [exportSearchTerm, setExportSearchTerm] = useState('');
  
  // États pour les notifications
  const [alertMessage, setAlertMessage] = useState({ show: false, message: '', type: 'info' });

  const getAuthToken = () => sessionStorage.getItem("authToken");
  
  const getHeaders = () => {
    const token = getAuthToken();
    const headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  };

  const showAlert = (message, type = 'info', duration = 4000) => {
    setAlertMessage({ show: true, message, type });
    if (duration > 0) {
      setTimeout(() => setAlertMessage(prev => ({ ...prev, show: false })), duration);
    }
  };

  // Formater la devise
  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return num.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' ' + defaultCurrency;
  };

  // ✅ NOUVEAU : Nettoyer un nom pour l'utiliser dans un nom de fichier
  const sanitizeFileName = (str) => {
    return (str || '')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // enlever les accents
      .replace(/[^a-zA-Z0-9]/g, '_')                    // remplacer caractères spéciaux par _
      .replace(/_+/g, '_')                              // éviter les __ multiples
      .replace(/^_|_$/g, '');                           // enlever les _ en début/fin
  };

  // Charger les quartiers
  const fetchQuartiers = async () => {
    try {
      setLoadingQuartiers(true);
      const response = await axios.get(LOCALITES_ENDPOINT, { headers: getHeaders() });
      
      let allData = [];
      if (response.data && Array.isArray(response.data)) {
        allData = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        allData = response.data.data;
      }
      
      const quartiersData = allData.filter(l => l.type_code === 1002);
      setQuartiers(quartiersData);
    } catch (error) {
      console.error("Erreur chargement quartiers:", error);
      setQuartiers([]);
      showAlert("Erreur de chargement des quartiers", 'warning');
    } finally {
      setLoadingQuartiers(false);
    }
  };

  // Charger les marchés
  const fetchMarches = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: currentPage,
        per_page: itemsPerPage,
        sort_field: sortField,
        sort_direction: sortDirection
      };
      
      if (filterText) params.search = filterText;
      
      const response = await axios.get(MARCHES_ENDPOINT, { 
        headers: getHeaders(),
        params: params
      });
      
      if (response.data && response.data.code === 200) {
        setMarches(response.data.data || []);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      } else {
        setMarches([]);
      }
    } catch (error) {
      console.log("Erreur chargement marchés:", error);
      setError(error.response?.data?.message || "Erreur de chargement");
      setMarches([]);
      showAlert("Erreur de chargement des marchés", 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Obtenir le nom du quartier
  const getQuartierName = (quartierId) => {
    if (!quartierId) return "-";
    const quartier = quartiers.find(q => q.localite_id === quartierId);
    return quartier ? quartier.nom : "Quartier inconnu";
  };

  // Filtrer les quartiers
  const getFilteredQuartiers = (searchTerm) => {
    if (!searchTerm) return quartiers;
    return quartiers.filter(q => 
      q.nom.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Sélectionner un quartier
  const handleSelectQuartier = (quartierId, quartierNom) => {
    setNewMarche(prev => ({ ...prev, id_quartier: quartierId }));
    setSelectedQuartierName(quartierNom);
    setQuartierSearchTerm(quartierNom);
    setShowQuartierDropdown(false);
  };

  const handleEditSelectQuartier = (quartierId, quartierNom) => {
    setEditedMarche(prev => ({ ...prev, id_quartier: quartierId }));
    setEditSelectedQuartierName(quartierNom);
    setEditQuartierSearchTerm(quartierNom);
    setShowEditQuartierDropdown(false);
  };

  // Filtrer et trier (client-side pour l'affichage)
  const filteredAndSortedItems = React.useMemo(() => {
    let filtered = [...marches];
    
    if (filterText) {
      const searchTerm = filterText.toLowerCase();
      filtered = filtered.filter(item => 
        (item.nom_marche && item.nom_marche.toLowerCase().includes(searchTerm)) ||
        (item.code_marche && item.code_marche.toLowerCase().includes(searchTerm)) ||
        (item.id_quartier && getQuartierName(item.id_quartier).toLowerCase().includes(searchTerm))
      );
    }

    filtered.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      
      if (sortField === 'quartier_nom') {
        aValue = getQuartierName(a.id_quartier);
        bValue = getQuartierName(b.id_quartier);
      }
      
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [marches, filterText, sortField, sortDirection, quartiers]);

  // Statistiques
  const stats = React.useMemo(() => {
    const total = marches.length;
    const ouverts = marches.filter(m => m.etat === "Ouvert").length;
    const fermes = marches.filter(m => m.etat === "Fermé").length;
    const quartiersMap = {};
    marches.forEach(m => {
      const qName = getQuartierName(m.id_quartier);
      if (qName !== "Quartier inconnu" && qName !== "-") {
        quartiersMap[qName] = (quartiersMap[qName] || 0) + 1;
      }
    });
    const topQuartier = Object.keys(quartiersMap).length > 0 
      ? Object.keys(quartiersMap).reduce((a, b) => quartiersMap[a] > quartiersMap[b] ? a : b)
      : "Aucun";
    
    return { total, ouverts, fermes, topQuartier, quartiersCount: Object.keys(quartiersMap).length };
  }, [marches, quartiers]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    fetchMarches();
  };

  // Générer le code du marché automatiquement
  const generateCodeMarche = () => {
    const count = marches.length + 1;
    return `MARCHE-${String(count).padStart(3, '0')}`;
  };

  // CRUD Operations
  const handleAddMarche = async () => {
    if (!newMarche.id_quartier) {
      showAlert('Veuillez sélectionner un quartier', 'warning');
      return;
    }
    if (!newMarche.nom_marche.trim()) {
      showAlert('Le nom du marché est requis', 'warning');
      return;
    }

    try {
      setLoadingAdd(true);
      
      const payload = {
        nom_marche: newMarche.nom_marche.trim(),
        id_quartier: newMarche.id_quartier,
        superficie: newMarche.superficie ? parseFloat(newMarche.superficie) : null,
        etat: newMarche.etat || "Ouvert",
        observations: newMarche.observations || ""
      };
      
      await axios.post(MARCHES_ENDPOINT, payload, { headers: getHeaders() });
      
      showAlert('Marché ajouté avec succès', 'success');
      setShowAddModal(false);
      setNewMarche({
        code_marche: "",
        nom_marche: "",
        id_quartier: "",
        superficie: "",
        etat: "Ouvert",
        observations: ""
      });
      setSelectedQuartierName('');
      setQuartierSearchTerm('');
      fetchMarches();
      
    } catch (error) {
      console.error("Erreur ajout marché:", error);
      showAlert(error.response?.data?.message || "Erreur lors de l'ajout", 'danger');
    } finally {
      setLoadingAdd(false);
    }
  };

  const handleEditMarche = (marche) => {
    setSelectedMarche(marche);
    const quartierName = getQuartierName(marche.id_quartier);
    setEditedMarche({
      id: marche.id,
      code_marche: marche.code_marche || "",
      nom_marche: marche.nom_marche || "",
      id_quartier: marche.id_quartier || "",
      superficie: marche.superficie || "",
      etat: marche.etat || "Ouvert",
      observations: marche.observations || ""
    });
    setEditSelectedQuartierName(quartierName);
    setEditQuartierSearchTerm(quartierName);
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editedMarche) return;
    if (!editedMarche.id_quartier) {
      showAlert('Veuillez sélectionner un quartier', 'warning');
      return;
    }
    if (!editedMarche.nom_marche.trim()) {
      showAlert('Le nom du marché est requis', 'warning');
      return;
    }

    try {
      setLoadingUpdate(true);
      
      const payload = {
        nom_marche: editedMarche.nom_marche.trim(),
        id_quartier: editedMarche.id_quartier,
        superficie: editedMarche.superficie ? parseFloat(editedMarche.superficie) : null,
        etat: editedMarche.etat || "Ouvert",
        observations: editedMarche.observations || ""
      };
      
      await axios.put(`${MARCHES_ENDPOINT}/${editedMarche.id}`, payload, { headers: getHeaders() });
      
      showAlert('Marché modifié avec succès', 'success');
      setShowEditModal(false);
      fetchMarches();
      
    } catch (error) {
      console.error("Erreur modification marché:", error);
      showAlert(error.response?.data?.message || "Erreur lors de la modification", 'danger');
    } finally {
      setLoadingUpdate(false);
    }
  };

  const handleDeleteMarche = (marche) => {
    setMarcheToDelete(marche);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!marcheToDelete) return;

    try {
      setLoadingDelete(true);
      
      await axios.delete(`${MARCHES_ENDPOINT}/${marcheToDelete.id}`, { headers: getHeaders() });
      
      showAlert('Marché supprimé avec succès', 'success');
      setShowDeleteModal(false);
      setMarcheToDelete(null);
      fetchMarches();
      
    } catch (error) {
      console.error("Erreur suppression marché:", error);
      showAlert(error.response?.data?.message || "Erreur lors de la suppression", 'danger');
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleViewDetails = (marche) => {
    setSelectedMarche(marche);
    setShowDetailsModal(true);
  };

  // ==================== GESTION SÉLECTION MARCHÉS POUR EXPORT ====================
  
  const toggleMarcheSelection = (marcheId) => {
    setSelectedMarchesForExport(prev => 
      prev.includes(marcheId) 
        ? prev.filter(id => id !== marcheId)
        : [...prev, marcheId]
    );
  };

  const selectAllMarchesForExport = () => {
    const filtered = getFilteredMarchesForExport();
    if (selectedMarchesForExport.length === filtered.length && filtered.length > 0) {
      setSelectedMarchesForExport([]);
    } else {
      setSelectedMarchesForExport(filtered.map(m => m.id));
    }
  };

  const getFilteredMarchesForExport = () => {
    if (!exportSearchTerm) return marches;
    const term = exportSearchTerm.toLowerCase();
    return marches.filter(m => 
      (m.nom_marche && m.nom_marche.toLowerCase().includes(term)) ||
      (m.code_marche && m.code_marche.toLowerCase().includes(term)) ||
      (getQuartierName(m.id_quartier).toLowerCase().includes(term))
    );
  };

  // ==================== EXPORT EXCEL ====================

  // Récupérer tous les contribuables d'un marché
  const fetchContribuablesByMarche = async (marcheId) => {
    try {
      const params = new URLSearchParams();
      params.append('all', 'true');
      params.append('id_marche', marcheId);
      
      const response = await axios.get(
        `${TAXPAYERS_ENDPOINT}/liste?${params.toString()}`,
        { headers: getHeaders() }
      );
      
      if (response.data && response.data.success) {
        return response.data.data || [];
      }
      return [];
    } catch (error) {
      console.error(`Erreur récupération contribuables marché ${marcheId}:`, error);
      return [];
    }
  };

  // Récupérer les données de taxe pour un marché
  const fetchTaxDataForMarche = async (marcheId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/rapports/complet`, {
        headers: getHeaders(),
        timeout: 20000,
        params: {
          start_date: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
          end_date: new Date().toISOString().split('T')[0],
          id_marche: marcheId,
          contribuables_page: 1,
          contribuables_per_page: 5000
        }
      });
      
      if (response.data.success && response.data.contribuables?.data) {
        return response.data.contribuables.data;
      }
      return [];
    } catch (error) {
      console.warn(`⚠️ Taxes non disponibles pour marché ${marcheId}, export sans taxes:`, error.message);
      return [];
    }
  };

  // Export des contribuables d'un marché individuel en Excel
  const exportContribuablesByMarche = async (marche) => {
    setExportMarcheLoading(marche.id);
    
    try {
      const marcheId = marche.id_marche || marche.id;
      
      // 1. Récupérer les contribuables du marché
      const contribuables = await fetchContribuablesByMarche(marcheId);
      
      if (!contribuables || contribuables.length === 0) {
        showAlert(`Aucun contribuable trouvé pour le marché "${marche.nom_marche}"`, 'warning');
        setExportMarcheLoading(null);
        return;
      }
      
      console.log(`📊 ${contribuables.length} contribuables récupérés pour le marché ${marche.nom_marche}`);
      
      // 2. Récupérer les données de taxe
      const taxData = await fetchTaxDataForMarche(marcheId);
      const taxMap = {};
      taxData.forEach(item => {
        if (item.contribuable_id) {
          taxMap[String(item.contribuable_id)] = {
            totalTaxe: parseFloat(item.montantInitialTotal || item.montant_total || 0),
            totalTaxeCollecte: parseFloat(item.montantPayeTotal || item.montant_paye || 0),
            totalTaxeNonCollecte: parseFloat(item.montantRestant || item.totalRestant || 0),
            nombreSouscriptions: item.nombreSouscriptions || 0
          };
        }
      });
      
      // 3. Construire les lignes Excel
      let totalTaxeGlobal = 0;
      let totalCollecteGlobal = 0;
      let totalNonCollecteGlobal = 0;
      
      const excelRows = contribuables.map((item, index) => {
        const taxInfo = taxMap[String(item.contribuable_id)] || {};
        const totalTaxe = taxInfo.totalTaxe || 0;
        const totalCollecte = taxInfo.totalTaxeCollecte || 0;
        const totalNonCollecte = taxInfo.totalTaxeNonCollecte || 0;
        
        totalTaxeGlobal += totalTaxe;
        totalCollecteGlobal += totalCollecte;
        totalNonCollecteGlobal += totalNonCollecte;
        
        let type = "Entreprise";
        if (item.activite_contribuable) {
          const activiteStr = item.activite_contribuable.toString();
          if (activiteStr.startsWith("1")) type = "Particulier";
          else if (activiteStr.startsWith("2")) type = "Commerçant";
          else if (activiteStr.startsWith("3")) type = "Professionnel";
        }
        
        let status = "Actif";
        if (item.is_active === -1) status = "Supprimé";
        else if (item.is_active === 0) status = "Inactif";
        
        return {
          'N°': index + 1,
          'ID Contribuable': item.contribuable_id || '',
          'Nom': item.nom_contribuable || '',
          'Téléphone': item.telephone || '',
          'Email': item.email || item.admin_email || '',
          'Adresse': item.adresse || '',
          'Type': type,
          'Statut': status,
          'Marché': marche.nom_marche || '',
          'Quartier': getQuartierName(marche.id_quartier),
          'Date inscription': item.created_at 
            ? new Date(item.created_at).toLocaleDateString('fr-FR') 
            : '',
          'Total Taxe (Prévu)': formatCurrency(totalTaxe),
          'Total Collecté': formatCurrency(totalCollecte),
          'Reste à payer': formatCurrency(totalNonCollecte),
          'Taux (%)': totalTaxe > 0 ? ((totalCollecte / totalTaxe) * 100).toFixed(2) : '0.00'
        };
      });
      
      // 4. Ajouter la ligne de synthèse
      const tauxGlobal = totalTaxeGlobal > 0 ? (totalCollecteGlobal / totalTaxeGlobal) * 100 : 0;
      excelRows.push({
        'N°': '',
        'ID Contribuable': '',
        'Nom': '=== SYNTHÈSE ===',
        'Téléphone': '',
        'Email': '',
        'Adresse': '',
        'Type': '',
        'Statut': '',
        'Marché': '',
        'Quartier': '',
        'Date inscription': '',
        'Total Taxe (Prévu)': formatCurrency(totalTaxeGlobal),
        'Total Collecté': formatCurrency(totalCollecteGlobal),
        'Reste à payer': formatCurrency(totalNonCollecteGlobal),
        'Taux (%)': tauxGlobal.toFixed(2)
      });
      
      // 5. Créer le fichier Excel
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelRows);
      
      ws['!cols'] = [
        { wch: 6 }, { wch: 16 }, { wch: 30 }, { wch: 16 }, { wch: 25 },
        { wch: 30 }, { wch: 15 }, { wch: 12 }, { wch: 25 }, { wch: 20 },
        { wch: 18 }, { wch: 22 }, { wch: 22 }, { wch: 22 }, { wch: 12 }
      ];
      
      // ✅ Nom de la feuille avec le nom du marché (max 31 caractères pour Excel)
      const sheetName = sanitizeFileName(marche.nom_marche).substring(0, 25) || "Contribuables";
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      
      // Feuille de synthèse
      const syntheseData = [
        { 'Indicateur': 'Marché', 'Valeur': marche.nom_marche || '' },
        { 'Indicateur': 'Code marché', 'Valeur': marche.code_marche || '-' },
        { 'Indicateur': 'ID Marché', 'Valeur': marcheId || '-' },
        { 'Indicateur': 'Quartier', 'Valeur': getQuartierName(marche.id_quartier) },
        { 'Indicateur': 'État', 'Valeur': marche.etat || '' },
        { 'Indicateur': 'Superficie', 'Valeur': marche.superficie ? `${parseFloat(marche.superficie).toFixed(2)} m²` : '-' },
        { 'Indicateur': '', 'Valeur': '' },
        { 'Indicateur': 'Nombre de contribuables', 'Valeur': contribuables.length },
        { 'Indicateur': '', 'Valeur': '' },
        { 'Indicateur': 'TOTAL TAXE PRÉVUE', 'Valeur': formatCurrency(totalTaxeGlobal) },
        { 'Indicateur': 'TOTAL COLLECTÉ', 'Valeur': formatCurrency(totalCollecteGlobal) },
        { 'Indicateur': 'RESTE À PAYER', 'Valeur': formatCurrency(totalNonCollecteGlobal) },
        { 'Indicateur': 'TAUX DE RECOUVREMENT', 'Valeur': tauxGlobal.toFixed(2) + '%' },
        { 'Indicateur': '', 'Valeur': '' },
        { 'Indicateur': 'Date de génération', 'Valeur': new Date().toLocaleString('fr-FR') }
      ];
      const syntheseSheet = XLSX.utils.json_to_sheet(syntheseData);
      syntheseSheet['!cols'] = [{ wch: 35 }, { wch: 30 }];
      XLSX.utils.book_append_sheet(wb, syntheseSheet, "Synthèse");
      
      // 6. ✅ Construire un nom de fichier propre avec le nom du marché
      const safeName = sanitizeFileName(marche.nom_marche) || `marche_${marcheId}`;
      const dateStr = new Date().toISOString().split('T')[0];
      
      // Nom final : "Contribuables_NomDuMarche_2026-09-11.xlsx"
      const fileName = `Contribuables_${safeName}_${dateStr}.xlsx`;
      
      XLSX.writeFile(wb, fileName);
      
      showAlert(`✅ Export terminé : ${contribuables.length} contribuables du marché "${marche.nom_marche}"`, 'success');
      
    } catch (error) {
      console.error("Erreur export marché:", error);
      showAlert(`Erreur lors de l'export : ${error.message}`, 'danger');
    } finally {
      setExportMarcheLoading(null);
    }
  };

  // Export de plusieurs marchés sélectionnés
  const exportSelectedMarches = async () => {
    if (selectedMarchesForExport.length === 0) {
      showAlert("Veuillez sélectionner au moins un marché", 'warning');
      return;
    }
    
    setExportLoading(true);
    
    try {
      // Récupérer les marchés sélectionnés
      const marchesToExport = marches.filter(m => selectedMarchesForExport.includes(m.id));
      
      if (marchesToExport.length === 0) {
        showAlert("Aucun marché à exporter", 'warning');
        setExportLoading(false);
        return;
      }
      
      console.log(`📊 Export de ${marchesToExport.length} marché(s)`);
      
      // Récupérer tous les contribuables de tous les marchés sélectionnés
      const allContribuables = [];
      const marcheMap = {};
      
      for (const marche of marchesToExport) {
        try {
          const marcheId = marche.id_marche || marche.id;
          const contribuables = await fetchContribuablesByMarche(marcheId);
          
          // Récupérer aussi les taxes pour ce marché
          const taxData = await fetchTaxDataForMarche(marcheId);
          const taxMap = {};
          taxData.forEach(item => {
            if (item.contribuable_id) {
              taxMap[String(item.contribuable_id)] = {
                totalTaxe: parseFloat(item.montantInitialTotal || item.montant_total || 0),
                totalTaxeCollecte: parseFloat(item.montantPayeTotal || item.montant_paye || 0),
                totalTaxeNonCollecte: parseFloat(item.montantRestant || item.totalRestant || 0)
              };
            }
          });
          
          contribuables.forEach(c => {
            const taxInfo = taxMap[String(c.contribuable_id)] || {};
            allContribuables.push({
              ...c,
              _marche_nom: marche.nom_marche,
              _marche_code: marche.code_marche,
              _marche_id: marche.id,
              _quartier: getQuartierName(marche.id_quartier),
              _totalTaxe: taxInfo.totalTaxe || 0,
              _totalCollecte: taxInfo.totalTaxeCollecte || 0,
              _totalNonCollecte: taxInfo.totalTaxeNonCollecte || 0
            });
          });
          
          marcheMap[marche.id] = {
            nom: marche.nom_marche,
            code: marche.code_marche,
            quartier: getQuartierName(marche.id_quartier),
            count: contribuables.length,
            totalTaxe: contribuables.reduce((sum, c) => {
              const t = taxMap[String(c.contribuable_id)] || {};
              return sum + (t.totalTaxe || 0);
            }, 0),
            totalCollecte: contribuables.reduce((sum, c) => {
              const t = taxMap[String(c.contribuable_id)] || {};
              return sum + (t.totalTaxeCollecte || 0);
            }, 0),
            totalNonCollecte: contribuables.reduce((sum, c) => {
              const t = taxMap[String(c.contribuable_id)] || {};
              return sum + (t.totalTaxeNonCollecte || 0);
            }, 0)
          };
          
        } catch (err) {
          console.warn(`Erreur marché ${marche.nom_marche}:`, err);
        }
      }
      
      if (allContribuables.length === 0) {
        showAlert("Aucun contribuable trouvé dans les marchés sélectionnés", 'warning');
        setExportLoading(false);
        return;
      }
      
      // Calculer les totaux globaux
      const totalTaxeGlobal = allContribuables.reduce((s, c) => s + (c._totalTaxe || 0), 0);
      const totalCollecteGlobal = allContribuables.reduce((s, c) => s + (c._totalCollecte || 0), 0);
      const totalNonCollecteGlobal = allContribuables.reduce((s, c) => s + (c._totalNonCollecte || 0), 0);
      const tauxGlobal = totalTaxeGlobal > 0 ? (totalCollecteGlobal / totalTaxeGlobal) * 100 : 0;
      
      // Construire les lignes
      const excelRows = allContribuables.map((item, index) => {
        let type = "Entreprise";
        if (item.activite_contribuable) {
          const activiteStr = item.activite_contribuable.toString();
          if (activiteStr.startsWith("1")) type = "Particulier";
          else if (activiteStr.startsWith("2")) type = "Commerçant";
          else if (activiteStr.startsWith("3")) type = "Professionnel";
        }
        
        let status = "Actif";
        if (item.is_active === -1) status = "Supprimé";
        else if (item.is_active === 0) status = "Inactif";
        
        const totalTaxe = item._totalTaxe || 0;
        const totalCollecte = item._totalCollecte || 0;
        const totalNonCollecte = item._totalNonCollecte || 0;
        
        return {
          'N°': index + 1,
          'ID Contribuable': item.contribuable_id || '',
          'Nom': item.nom_contribuable || '',
          'Téléphone': item.telephone || '',
          'Email': item.email || item.admin_email || '',
          'Adresse': item.adresse || '',
          'Type': type,
          'Statut': status,
          'Marché': item._marche_nom || '',
          'Code marché': item._marche_code || '',
          'Quartier': item._quartier || '',
          'Date inscription': item.created_at 
            ? new Date(item.created_at).toLocaleDateString('fr-FR') 
            : '',
          'Total Taxe (Prévu)': formatCurrency(totalTaxe),
          'Total Collecté': formatCurrency(totalCollecte),
          'Reste à payer': formatCurrency(totalNonCollecte),
          'Taux (%)': totalTaxe > 0 ? ((totalCollecte / totalTaxe) * 100).toFixed(2) : '0.00'
        };
      });
      
      // Ligne de synthèse
      excelRows.push({
        'N°': '',
        'ID Contribuable': '',
        'Nom': '=== SYNTHÈSE GLOBALE ===',
        'Téléphone': '',
        'Email': '',
        'Adresse': '',
        'Type': '',
        'Statut': '',
        'Marché': '',
        'Code marché': '',
        'Quartier': '',
        'Date inscription': '',
        'Total Taxe (Prévu)': formatCurrency(totalTaxeGlobal),
        'Total Collecté': formatCurrency(totalCollecteGlobal),
        'Reste à payer': formatCurrency(totalNonCollecteGlobal),
        'Taux (%)': tauxGlobal.toFixed(2)
      });
      
      // Créer le fichier
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelRows);
      ws['!cols'] = [
        { wch: 6 }, { wch: 16 }, { wch: 30 }, { wch: 16 }, { wch: 25 },
        { wch: 30 }, { wch: 15 }, { wch: 12 }, { wch: 25 }, { wch: 15 }, { wch: 20 }, { wch: 18 },
        { wch: 22 }, { wch: 22 }, { wch: 22 }, { wch: 12 }
      ];
      XLSX.utils.book_append_sheet(wb, ws, "Contribuables");
      
      // Feuille synthèse par marché
      const syntheseRows = Object.entries(marcheMap).map(([id, info]) => ({
        'Marché': info.nom,
        'Code': info.code || '-',
        'Quartier': info.quartier,
        'Nb contribuables': info.count,
        'Total Taxe Prévu': formatCurrency(info.totalTaxe),
        'Total Collecté': formatCurrency(info.totalCollecte),
        'Reste à payer': formatCurrency(info.totalNonCollecte),
        'Taux (%)': info.totalTaxe > 0 ? ((info.totalCollecte / info.totalTaxe) * 100).toFixed(2) : '0.00'
      }));
      
      // Ligne de total global
      syntheseRows.push({
        'Marché': '=== TOTAL GLOBAL ===',
        'Code': '',
        'Quartier': '',
        'Nb contribuables': allContribuables.length,
        'Total Taxe Prévu': formatCurrency(totalTaxeGlobal),
        'Total Collecté': formatCurrency(totalCollecteGlobal),
        'Reste à payer': formatCurrency(totalNonCollecteGlobal),
        'Taux (%)': tauxGlobal.toFixed(2)
      });
      
      const syntheseSheet = XLSX.utils.json_to_sheet(syntheseRows);
      syntheseSheet['!cols'] = [
        { wch: 30 }, { wch: 15 }, { wch: 25 }, { wch: 18 },
        { wch: 22 }, { wch: 22 }, { wch: 22 }, { wch: 12 }
      ];
      XLSX.utils.book_append_sheet(wb, syntheseSheet, "Synthèse par marché");
      
      // ✅ NOUVEAU : Nom du fichier avec les noms des marchés
      const dateStr = new Date().toISOString().split('T')[0];
      
      // Construire la partie descriptive avec les noms des marchés
      let marchesPart = '';
      if (marchesToExport.length === 1) {
        // 1 marché → son nom
        marchesPart = sanitizeFileName(marchesToExport[0].nom_marche);
      } else if (marchesToExport.length <= 3) {
        // 2-3 marchés → noms séparés par _
        marchesPart = marchesToExport
          .map(m => sanitizeFileName(m.nom_marche))
          .join('_');
      } else {
        // Plus de 3 → "Premier_Deuxieme_et_X_autres"
        const firstTwo = marchesToExport
          .slice(0, 2)
          .map(m => sanitizeFileName(m.nom_marche))
          .join('_');
        marchesPart = `${firstTwo}_et_${marchesToExport.length - 2}_autres`;
      }
      
      // Limiter la longueur (sécurité Windows)
      const MAX_LEN = 100;
      if (marchesPart.length > MAX_LEN) {
        marchesPart = marchesPart.substring(0, MAX_LEN);
      }
      
      // Nom final : "Contribuables_NomMarche1_NomMarche2_2026-09-11.xlsx"
      const fileName = `Contribuables_${marchesPart}_${dateStr}.xlsx`;
      
      XLSX.writeFile(wb, fileName);
      
      showAlert(`✅ Export terminé : ${allContribuables.length} contribuables de ${marchesToExport.length} marché(s)`, 'success');
      setShowExportModal(false);
      setSelectedMarchesForExport([]);
      setExportSearchTerm('');
      
    } catch (error) {
      console.error("Erreur export global:", error);
      showAlert(`Erreur lors de l'export : ${error.message}`, 'danger');
    } finally {
      setExportLoading(false);
    }
  };

  // ==================== FIN EXPORT EXCEL ====================

  const SortableHeader = ({ field, children }) => (
    <th 
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => handleSort(field)}
    >
      {children}
      {sortField === field && (
        <span className="ms-1">
          {sortDirection === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </span>
      )}
    </th>
  );

  // Chargement initial
  useEffect(() => {
    fetchQuartiers();
    fetchMarches();
  }, []);

  // Recharger quand les filtres changent
  useEffect(() => {
    if (!loading) {
      fetchMarches();
    }
  }, [currentPage, itemsPerPage, sortField, sortDirection, filterText]);

  // Gestion des clics en dehors du dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (quartierSearchRef.current && !quartierSearchRef.current.contains(event.target)) {
        setShowQuartierDropdown(false);
      }
      if (editQuartierSearchRef.current && !editQuartierSearchRef.current.contains(event.target)) {
        setShowEditQuartierDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Liste filtrée des marchés pour l'export
  const filteredMarchesForExport = React.useMemo(() => {
    return getFilteredMarchesForExport();
  }, [marches, exportSearchTerm, quartiers]);

  return (
    <React.Fragment>
      <Header />
      
      <ToastContainer className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '20px', right: '20px' }}>
        <Toast show={alertMessage.show} onClose={() => setAlertMessage(prev => ({ ...prev, show: false }))} bg={alertMessage.type} delay={4000} autohide>
          <Toast.Header><strong className="me-auto">Notification</strong></Toast.Header>
          <Toast.Body className="text-white">{alertMessage.message}</Toast.Body>
        </Toast>
      </ToastContainer>
      
      <div className="main main-app p-3 p-lg-4" style={{ background: '#f0f2f5', minHeight: '100vh' }}>
        <div className="container-fluid">
          
          {/* En-tête */}
          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                <div className="mb-3 mb-md-0">
                  <ol className="breadcrumb fs-sm mb-1">
                    <li className="breadcrumb-item"><Link to="/admin/dashboard">Tableau de bord</Link></li>
                    <li className="breadcrumb-item active">Marchés</li>
                  </ol>
                  <h4 className="main-title mb-0 d-flex align-items-center">
                    <Building className="me-2 text-primary" size={28} />
                    Gestion des Marchés
                  </h4>
                  <p className="text-muted mb-0">Gérez les marchés par quartier</p>
                </div>

                <div className="d-flex gap-2 flex-wrap">
                  <Button 
                    variant="outline-success" 
                    className="d-flex align-items-center shadow-sm"
                    onClick={() => {
                      setShowExportModal(true);
                      setSelectedMarchesForExport([]);
                      setExportSearchTerm('');
                    }}
                    disabled={loading || marches.length === 0}
                  >
                    <FileExcel className="me-2" size={16} />
                    Exporter les contribuables
                  </Button>
               
                  <Link to="/gestion/marches/nouveau" className="text-decoration-none">
                    <Button 
                      variant="success" 
                      className="d-flex align-items-center shadow-sm"
                    >
                      <PlusLg className="me-2" size={16} />
                      Nouveau marché
                    </Button>
                  </Link>
                </div>
              </div>
            </Col>
          </Row>

          {/* Statistiques */}
          <Row className="g-3 mb-4">
            <Col md={3}>
              <Card className="border-0 shadow-sm rounded-3 h-100 stats-card">
                <div className="card-gradient bg-primary"></div>
                <Card.Body className="d-flex align-items-center">
                  <div className="bg-primary bg-opacity-10 p-3 rounded-3 me-3">
                    <Shop size={28} className="text-primary" />
                  </div>
                  <div>
                    <h6 className="text-muted mb-1">Total Marchés</h6>
                    <h3 className="mb-0 fw-bold">{stats.total}</h3>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm rounded-3 h-100 stats-card">
                <div className="card-gradient bg-success"></div>
                <Card.Body className="d-flex align-items-center">
                  <div className="bg-success bg-opacity-10 p-3 rounded-3 me-3">
                    <CheckCircle size={28} className="text-success" />
                  </div>
                  <div>
                    <h6 className="text-muted mb-1">Ouverts</h6>
                    <h3 className="mb-0 fw-bold">{stats.ouverts}</h3>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm rounded-3 h-100 stats-card">
                <div className="card-gradient bg-danger"></div>
                <Card.Body className="d-flex align-items-center">
                  <div className="bg-danger bg-opacity-10 p-3 rounded-3 me-3">
                    <XCircle size={28} className="text-danger" />
                  </div>
                  <div>
                    <h6 className="text-muted mb-1">Fermés</h6>
                    <h3 className="mb-0 fw-bold">{stats.fermes}</h3>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm rounded-3 h-100 stats-card">
                <div className="card-gradient bg-info"></div>
                <Card.Body className="d-flex align-items-center">
                  <div className="bg-info bg-opacity-10 p-3 rounded-3 me-3">
                    <GeoAlt size={28} className="text-info" />
                  </div>
                  <div>
                    <h6 className="text-muted mb-1">Quartiers couverts</h6>
                    <h3 className="mb-0 fw-bold">{stats.quartiersCount}</h3>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Barre de recherche */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="border-0 shadow-sm rounded-3">
                <Card.Body>
                  <Row className="g-3">
                    <Col md={7}>
                      <InputGroup>
                        <InputGroup.Text className="bg-light border-end-0">
                          <Search className="text-primary" />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Rechercher par nom, code ou quartier..."
                          value={filterText}
                          onChange={(e) => setFilterText(e.target.value)}
                          className="border-start-0 bg-light"
                        />
                        {filterText && (
                          <Button 
                            variant="outline-secondary" 
                            className="border-start-0"
                            onClick={() => setFilterText('')}
                          >
                            <X size={16} />
                          </Button>
                        )}
                      </InputGroup>
                    </Col>
                    <Col md={3}>
                      <Form.Select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="bg-light"
                      >
                        <option value="5">5 / page</option>
                        <option value="10">10 / page</option>
                        <option value="25">25 / page</option>
                        <option value="50">50 / page</option>
                      </Form.Select>
                    </Col>
                    <Col md={2}>
                      <Button 
                        variant="outline-primary" 
                        onClick={fetchMarches} 
                        className="w-100 d-flex align-items-center justify-content-center"
                      >
                        <Cloud className="me-2" size={16} />
                        Rafraîchir
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Tableau */}
          <Row>
            <Col md={12}>
              <Card className="shadow-sm border-0 rounded-3">
                <Card.Header className="bg-primary d-flex justify-content-between align-items-center py-3 border-bottom">
                  <div className="d-flex align-items-center">
                    <Building className="me-2 text-white" />
                    <span className="fw-semibold text-white">Liste des marchés ({filteredAndSortedItems.length})</span>
                    {filterText && (
                      <Badge bg="info" className="ms-2">
                        Filtre actif
                      </Badge>
                    )}
                  </div>
                  <div className="text-white small">
                    {filteredAndSortedItems.length > 0 && (
                      <span>Affichage de {startIndex + 1} à {Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)}</span>
                    )}
                  </div>
                </Card.Header>
                
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table hover className="mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th style={{ width: '60px' }}>#</th>
                          <SortableHeader field="code_marche">Code</SortableHeader>
                          <SortableHeader field="id_marche">ID Marché</SortableHeader>
                          <SortableHeader field="nom_marche">Nom du marché</SortableHeader>
                          <SortableHeader field="quartier_nom">Quartier</SortableHeader>
                          <SortableHeader field="superficie">Superficie</SortableHeader>
                          <SortableHeader field="etat">État</SortableHeader>
                          <th style={{ width: '180px' }} className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="8" className="text-center py-5">
                              <Spinner animation="border" variant="primary" className="me-3" />
                              <span className="text-muted">Chargement...</span>
                            </td>
                          </tr>
                        ) : currentItems.length > 0 ? (
                          currentItems.map((marche, index) => (
                            <tr key={marche.id} className="align-middle">
                              <td className="text-muted">{startIndex + index + 1}</td>
                              <td>
                                <code className="text-primary">{marche.code_marche || '-'}</code>
                              </td>
                              <td>
                                <code className="text-success">{marche.id_marche || '-'}</code>
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <Shop size={18} className="text-primary me-2" />
                                  <strong>{marche.nom_marche}</strong>
                                </div>
                              </td>
                              <td>
                                <Badge bg="primary" className="px-3 py-2 rounded-pill">
                                  <GeoAlt className="me-1" size={12} />
                                  {getQuartierName(marche.id_quartier)}
                                </Badge>
                              </td>
                              <td>
                                {marche.superficie ? (
                                  <span className="text-muted">{parseFloat(marche.superficie).toFixed(2)} m²</span>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td>
                                <Badge bg={marche.etat === "Ouvert" ? "success" : "danger"} className="px-3 py-2 rounded-pill">
                                  {marche.etat === "Ouvert" ? (
                                    <CheckCircle className="me-1" size={12} />
                                  ) : (
                                    <XCircle className="me-1" size={12} />
                                  )}
                                  {marche.etat || "Inconnu"}
                                </Badge>
                              </td>
                              <td>
                                <div className="d-flex gap-1 justify-content-center">
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    title="Voir détails"
                                    onClick={() => handleViewDetails(marche)}
                                    className="rounded-circle"
                                    style={{ width: '34px', height: '34px', padding: 0 }}
                                  >
                                    <Eye size={14} />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-warning"
                                    title="Modifier"
                                    onClick={() => handleEditMarche(marche)}
                                    className="rounded-circle"
                                    style={{ width: '34px', height: '34px', padding: 0 }}
                                  >
                                    <Pencil size={14} />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-success"
                                    title="Exporter les contribuables de ce marché"
                                    onClick={() => exportContribuablesByMarche(marche)}
                                    disabled={exportMarcheLoading === marche.id}
                                    className="rounded-circle"
                                    style={{ width: '34px', height: '34px', padding: 0 }}
                                  >
                                    {exportMarcheLoading === marche.id ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      <FileExcel size={14} />
                                    )}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    title="Supprimer"
                                    onClick={() => handleDeleteMarche(marche)}
                                    className="rounded-circle"
                                    style={{ width: '34px', height: '34px', padding: 0 }}
                                  >
                                    <Trash size={14} />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center py-5">
                              <Shop size={48} className="text-muted opacity-25" />
                              <h5 className="mt-3 text-muted">AUCUN MARCHÉ TROUVÉ</h5>
                              <p className="text-muted small">Aucun marché ne correspond à vos critères</p>
                              {filterText && (
                                <Button variant="outline-primary" size="sm" onClick={() => setFilterText('')}>
                                  Réinitialiser la recherche
                                </Button>
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="p-3 border-top d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3">
                      <div className="text-muted small">
                        Affichage de <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)}</strong> sur <strong>{filteredAndSortedItems.length}</strong> marchés
                        {pagination.total > 0 && (
                          <span className="ms-2 text-primary">• Total: {pagination.total}</span>
                        )}
                      </div>
                      <Pagination className="mb-0">
                        <Pagination.Prev 
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage(currentPage - 1)}
                        />
                        {[...Array(Math.min(totalPages, 7))].map((_, index) => {
                          const pageNum = index + 1;
                          return (
                            <Pagination.Item
                              key={pageNum}
                              active={pageNum === currentPage}
                              onClick={() => setCurrentPage(pageNum)}
                              className={pageNum === currentPage ? 'fw-bold' : ''}
                            >
                              {pageNum}
                            </Pagination.Item>
                          );
                        })}
                        {totalPages > 7 && (
                          <>
                            <Pagination.Ellipsis />
                            <Pagination.Item onClick={() => setCurrentPage(totalPages)}>
                              {totalPages}
                            </Pagination.Item>
                          </>
                        )}
                        <Pagination.Next 
                          disabled={currentPage === totalPages}
                          onClick={() => setCurrentPage(currentPage + 1)}
                        />
                      </Pagination>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

        {/* MODAL D'AJOUT */}
        <Modal show={showAddModal} onHide={() => {
          setShowAddModal(false);
          setQuartierSearchTerm('');
          setSelectedQuartierName('');
          setShowQuartierDropdown(false);
        }} centered size="lg">
          <Modal.Header closeButton className="text-white" style={{ background: "linear-gradient(135deg, #198754, #157347)" }}>
            <Modal.Title className="d-flex align-items-center">
              <PlusLg className="me-2" />
              Nouveau marché
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row className="g-3">
                <Col md={6}>
                  <FloatingLabel label="Code marché" className="mb-3">
                    <Form.Control
                      type="text"
                      placeholder="Code marché"
                      value={newMarche.code_marche || generateCodeMarche()}
                      onChange={(e) => setNewMarche({ ...newMarche, code_marche: e.target.value })}
                    />
                    <Form.Text className="text-muted">Laissez vide pour auto-génération</Form.Text>
                  </FloatingLabel>
                </Col>
                <Col md={6}>
                  <FloatingLabel label="Nom du marché *" className="mb-3">
                    <Form.Control
                      type="text"
                      placeholder="Nom du marché"
                      value={newMarche.nom_marche}
                      onChange={(e) => setNewMarche({ ...newMarche, nom_marche: e.target.value })}
                      required
                    />
                  </FloatingLabel>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Quartier *</Form.Label>
                    <div ref={quartierSearchRef} className="position-relative">
                      <InputGroup>
                        <InputGroup.Text className="bg-light border-end-0">
                          <Search size={16} className="text-primary" />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Rechercher un quartier..."
                          value={quartierSearchTerm}
                          onChange={(e) => {
                            setQuartierSearchTerm(e.target.value);
                            setShowQuartierDropdown(true);
                            if (!e.target.value) {
                              setNewMarche(prev => ({ ...prev, id_quartier: "" }));
                              setSelectedQuartierName('');
                            }
                          }}
                          onFocus={() => setShowQuartierDropdown(true)}
                          className="border-start-0 bg-light"
                        />
                        {newMarche.id_quartier && (
                          <Button 
                            variant="outline-secondary" 
                            className="border-start-0"
                            onClick={() => {
                              setQuartierSearchTerm('');
                              setSelectedQuartierName('');
                              setNewMarche(prev => ({ ...prev, id_quartier: "" }));
                              setShowQuartierDropdown(false);
                            }}
                          >
                            <X size={16} />
                          </Button>
                        )}
                      </InputGroup>
                      
                      {showQuartierDropdown && (
                        <div 
                          className="position-absolute w-100 mt-1 bg-white rounded-3 shadow-lg border"
                          style={{ zIndex: 1000, maxHeight: '250px', overflowY: 'auto' }}
                        >
                          {loadingQuartiers ? (
                            <div className="p-3 text-center">
                              <Spinner animation="border" size="sm" />
                              <span className="ms-2">Chargement...</span>
                            </div>
                          ) : getFilteredQuartiers(quartierSearchTerm).length > 0 ? (
                            getFilteredQuartiers(quartierSearchTerm).map(quartier => (
                              <div
                                key={quartier.localite_id}
                                className="p-2 border-bottom hover-bg-light cursor-pointer d-flex justify-content-between align-items-center"
                                onClick={() => handleSelectQuartier(quartier.localite_id, quartier.nom)}
                                style={{ cursor: 'pointer' }}
                              >
                                <span className="fw-medium">
                                  {quartier.nom}
                                  {newMarche.id_quartier === quartier.localite_id && (
                                    <CheckCircle className="ms-2 text-success" size={14} />
                                  )}
                                </span>
                                <Badge bg="secondary">Quartier</Badge>
                              </div>
                            ))
                          ) : (
                            <div className="p-3 text-center text-muted">
                              <InfoCircle className="me-2" />
                              Aucun quartier trouvé
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {selectedQuartierName && (
                      <div className="mt-2">
                        <Badge bg="success" className="px-3 py-2 rounded-pill">
                          <CheckCircle className="me-1" size={12} />
                          Quartier sélectionné : {selectedQuartierName}
                        </Badge>
                      </div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <FloatingLabel label="Superficie (m²)" className="mb-3">
                    <Form.Control
                      type="number"
                      step="0.01"
                      placeholder="Superficie en m²"
                      value={newMarche.superficie}
                      onChange={(e) => setNewMarche({ ...newMarche, superficie: e.target.value })}
                    />
                  </FloatingLabel>
                </Col>
                <Col md={6}>
                  <FloatingLabel label="État" className="mb-3">
                    <Form.Select
                      value={newMarche.etat}
                      onChange={(e) => setNewMarche({ ...newMarche, etat: e.target.value })}
                    >
                      <option value="Ouvert">Ouvert</option>
                      <option value="Fermé">Fermé</option>
                    </Form.Select>
                  </FloatingLabel>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">Observations</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Observations"
                      value={newMarche.observations}
                      onChange={(e) => setNewMarche({ ...newMarche, observations: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {
              setShowAddModal(false);
              setQuartierSearchTerm('');
              setSelectedQuartierName('');
              setShowQuartierDropdown(false);
            }} disabled={loadingAdd}>
              Annuler
            </Button>
            <Button variant="success" onClick={handleAddMarche} disabled={loadingAdd || !newMarche.id_quartier || !newMarche.nom_marche.trim()}>
              {loadingAdd ? <><Spinner size="sm" className="me-2" />Enregistrement...</> : <><Check className="me-2" />Enregistrer</>}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* MODAL D'ÉDITION */}
        <Modal show={showEditModal} onHide={() => {
          setShowEditModal(false);
          setEditQuartierSearchTerm('');
          setEditSelectedQuartierName('');
          setShowEditQuartierDropdown(false);
        }} centered size="lg">
          <Modal.Header closeButton className="text-white" style={{ background: "linear-gradient(135deg, #0dcaf0, #0b5ed7)" }}>
            <Modal.Title className="d-flex align-items-center">
              <Pencil className="me-2" />
              Modifier le marché
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {editedMarche && (
              <Form>
                <Row className="g-3">
                  <Col md={6}>
                    <FloatingLabel label="Code marché" className="mb-3">
                      <Form.Control
                        type="text"
                        placeholder="Code marché"
                        value={editedMarche.code_marche || ''}
                        onChange={(e) => setEditedMarche({ ...editedMarche, code_marche: e.target.value })}
                        disabled
                      />
                      <Form.Text className="text-muted">Le code ne peut pas être modifié</Form.Text>
                    </FloatingLabel>
                  </Col>
                  <Col md={6}>
                    <FloatingLabel label="Nom du marché *" className="mb-3">
                      <Form.Control
                        type="text"
                        placeholder="Nom du marché"
                        value={editedMarche.nom_marche}
                        onChange={(e) => setEditedMarche({ ...editedMarche, nom_marche: e.target.value })}
                        required
                      />
                    </FloatingLabel>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Quartier *</Form.Label>
                      <div ref={editQuartierSearchRef} className="position-relative">
                        <InputGroup>
                          <InputGroup.Text className="bg-light border-end-0">
                            <Search size={16} className="text-primary" />
                          </InputGroup.Text>
                          <Form.Control
                            type="text"
                            placeholder="Rechercher un quartier..."
                            value={editQuartierSearchTerm}
                            onChange={(e) => {
                              setEditQuartierSearchTerm(e.target.value);
                              setShowEditQuartierDropdown(true);
                              if (!e.target.value) {
                                setEditedMarche(prev => ({ ...prev, id_quartier: "" }));
                                setEditSelectedQuartierName('');
                              }
                            }}
                            onFocus={() => setShowEditQuartierDropdown(true)}
                            className="border-start-0 bg-light"
                          />
                          {editedMarche.id_quartier && (
                            <Button 
                              variant="outline-secondary" 
                              className="border-start-0"
                              onClick={() => {
                                setEditQuartierSearchTerm('');
                                setEditSelectedQuartierName('');
                                setEditedMarche(prev => ({ ...prev, id_quartier: "" }));
                                setShowEditQuartierDropdown(false);
                              }}
                            >
                              <X size={16} />
                            </Button>
                          )}
                        </InputGroup>
                        
                        {showEditQuartierDropdown && (
                          <div 
                            className="position-absolute w-100 mt-1 bg-white rounded-3 shadow-lg border"
                            style={{ zIndex: 1000, maxHeight: '250px', overflowY: 'auto' }}
                          >
                            {loadingQuartiers ? (
                              <div className="p-3 text-center">
                                <Spinner animation="border" size="sm" />
                                <span className="ms-2">Chargement...</span>
                              </div>
                            ) : getFilteredQuartiers(editQuartierSearchTerm).length > 0 ? (
                              getFilteredQuartiers(editQuartierSearchTerm).map(quartier => (
                                <div
                                  key={quartier.localite_id}
                                  className="p-2 border-bottom hover-bg-light cursor-pointer d-flex justify-content-between align-items-center"
                                  onClick={() => handleEditSelectQuartier(quartier.localite_id, quartier.nom)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <span className="fw-medium">
                                    {quartier.nom}
                                    {editedMarche.id_quartier === quartier.localite_id && (
                                      <CheckCircle className="ms-2 text-success" size={14} />
                                    )}
                                  </span>
                                  <Badge bg="secondary">Quartier</Badge>
                                </div>
                              ))
                            ) : (
                              <div className="p-3 text-center text-muted">
                                <InfoCircle className="me-2" />
                                Aucun quartier trouvé
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {editSelectedQuartierName && (
                        <div className="mt-2">
                          <Badge bg="success" className="px-3 py-2 rounded-pill">
                            <CheckCircle className="me-1" size={12} />
                            Quartier sélectionné : {editSelectedQuartierName}
                          </Badge>
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <FloatingLabel label="Superficie (m²)" className="mb-3">
                      <Form.Control
                        type="number"
                        step="0.01"
                        placeholder="Superficie en m²"
                        value={editedMarche.superficie}
                        onChange={(e) => setEditedMarche({ ...editedMarche, superficie: e.target.value })}
                      />
                    </FloatingLabel>
                  </Col>
                  <Col md={6}>
                    <FloatingLabel label="État" className="mb-3">
                      <Form.Select
                        value={editedMarche.etat || "Ouvert"}
                        onChange={(e) => setEditedMarche({ ...editedMarche, etat: e.target.value })}
                      >
                        <option value="Ouvert">Ouvert</option>
                        <option value="Fermé">Fermé</option>
                      </Form.Select>
                    </FloatingLabel>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Observations</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="Observations"
                        value={editedMarche.observations || ''}
                        onChange={(e) => setEditedMarche({ ...editedMarche, observations: e.target.value })}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {
              setShowEditModal(false);
              setEditQuartierSearchTerm('');
              setEditSelectedQuartierName('');
              setShowEditQuartierDropdown(false);
            }} disabled={loadingUpdate}>
              Annuler
            </Button>
            <Button variant="info" className="text-white" onClick={handleSaveEdit} disabled={loadingUpdate || !editedMarche?.id_quartier || !editedMarche?.nom_marche?.trim()}>
              {loadingUpdate ? <><Spinner size="sm" className="me-2" />Enregistrement...</> : <><Check className="me-2" />Enregistrer</>}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* MODAL DE DÉTAILS */}
        <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} centered>
          <Modal.Header closeButton className="text-white" style={{ background: "linear-gradient(135deg, #0d6efd, #0a58ca)" }}>
            <Modal.Title className="d-flex align-items-center">
              <Eye className="me-2" />
              Détails du marché
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedMarche && (
              <div className="p-3">
                <div className="text-center mb-4">
                  <Shop size={56} className="text-primary" />
                  <h4 className="mt-2 fw-bold">{selectedMarche.nom_marche}</h4>
                  <div className="d-flex justify-content-center gap-2 mt-2">
                    <Badge bg="light" text="dark" className="mt-1">
                      ID: {selectedMarche.id}
                    </Badge>
                    <Badge bg="secondary" className="mt-1">
                      {selectedMarche.code_marche || 'Sans code'}
                    </Badge>
                  </div>
                </div>
                <Row className="g-3">
                  <Col md={6}>
                    <Card className="border-0 bg-light h-100">
                      <Card.Body>
                        <small className="text-muted d-block text-uppercase fw-semibold mb-1">Quartier</small>
                        <Badge bg="info" className="px-3 py-2 rounded-pill">
                          <GeoAlt className="me-1" />
                          {getQuartierName(selectedMarche.id_quartier)}
                        </Badge>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="border-0 bg-light h-100">
                      <Card.Body>
                        <small className="text-muted d-block text-uppercase fw-semibold mb-1">État</small>
                        <Badge bg={selectedMarche.etat === "Ouvert" ? "success" : "danger"} className="px-3 py-2 rounded-pill">
                          {selectedMarche.etat === "Ouvert" ? (
                            <CheckCircle className="me-1" size={14} />
                          ) : (
                            <XCircle className="me-1" size={14} />
                          )}
                          {selectedMarche.etat || "Inconnu"}
                        </Badge>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="border-0 bg-light h-100">
                      <Card.Body>
                        <small className="text-muted d-block text-uppercase fw-semibold mb-1">Superficie</small>
                        <p className="mb-0 fw-semibold">
                          {selectedMarche.superficie ? `${parseFloat(selectedMarche.superficie).toFixed(2)} m²` : '-'}
                        </p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="border-0 bg-light h-100">
                      <Card.Body>
                        <small className="text-muted d-block text-uppercase fw-semibold mb-1">ID Marché</small>
                        <p className="mb-0 fw-semibold">{selectedMarche.id_marche || '-'}</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={12}>
                    <Card className="border-0 bg-light h-100">
                      <Card.Body>
                        <small className="text-muted d-block text-uppercase fw-semibold mb-1">Observations</small>
                        <p className="mb-0">{selectedMarche.observations || 'Aucune observation'}</p>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <Button 
              variant="outline-success" 
              onClick={() => {
                setShowDetailsModal(false);
                if (selectedMarche) exportContribuablesByMarche(selectedMarche);
              }}
              disabled={!selectedMarche || exportMarcheLoading === selectedMarche?.id}
            >
              {exportMarcheLoading === selectedMarche?.id ? (
                <><Spinner size="sm" className="me-2" /> Export...</>
              ) : (
                <><FileExcel className="me-2" size={16} /> Exporter les contribuables</>
              )}
            </Button>
            <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
              Fermer
            </Button>
          </Modal.Footer>
        </Modal>

        {/* MODAL DE SUPPRESSION */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <ExclamationCircle className="text-warning me-2" />
              Confirmer la suppression
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center">
              <Trash className="display-4 text-danger mb-3" size={48} />
              <h5>Êtes-vous sûr de vouloir supprimer ce marché ?</h5>
              {marcheToDelete && (
                <div className="mt-3 p-3 bg-light rounded-3">
                  <strong>{marcheToDelete.nom_marche}</strong>
                  <br />
                  <small className="text-muted">Code: {marcheToDelete.code_marche || 'N/A'}</small>
                  <br />
                  <small className="text-muted">Quartier: {getQuartierName(marcheToDelete.id_quartier)}</small>
                </div>
              )}
              <p className="text-muted mt-3">Cette action est irréversible.</p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={loadingDelete}>
              <X className="me-2" />Annuler
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete} disabled={loadingDelete}>
              {loadingDelete ? <><Spinner animation="border" size="sm" className="me-2" />Suppression...</> : <><Trash className="me-2" />Supprimer</>}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* MODAL D'EXPORT PAR MARCHÉ (SÉLECTION MULTIPLE) */}
        <Modal 
          show={showExportModal} 
          onHide={() => {
            setShowExportModal(false);
            setSelectedMarchesForExport([]);
            setExportSearchTerm('');
          }} 
          size="lg" 
          centered
        >
          <Modal.Header closeButton className="bg-success text-white border-0">
            <Modal.Title>
              <FileExcel className="me-2" size={20} />
              Exporter les contribuables par marché
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {/* <div className="alert alert-info mb-4">
              <InfoCircle className="me-2" size={18} />
              Sélectionnez un ou plusieurs marchés pour exporter leurs contribuables dans un seul fichier Excel.
              <br />
              <small className="text-muted">Le fichier contiendra une feuille "Contribuables" et une feuille "Synthèse par marché".</small>
            </div> */}
            
            {/* Barre de recherche */}
            <Form.Group className="mb-3">
              <InputGroup>
                <InputGroup.Text className="bg-light border-end-0">
                  <Search className="text-primary" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Rechercher un marché..."
                  value={exportSearchTerm}
                  onChange={(e) => setExportSearchTerm(e.target.value)}
                  className="border-start-0 bg-light"
                />
                {exportSearchTerm && (
                  <Button 
                    variant="outline-secondary" 
                    className="border-start-0"
                    onClick={() => setExportSearchTerm('')}
                  >
                    <X size={16} />
                  </Button>
                )}
              </InputGroup>
            </Form.Group>
            
            {/* Boutons de sélection */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={selectAllMarchesForExport}
                  className="me-2"
                >
                  {selectedMarchesForExport.length === filteredMarchesForExport.length && filteredMarchesForExport.length > 0 ? (
                    <><CheckSquare className="me-1" size={14} /> Tout désélectionner</>
                  ) : (
                    <><Square className="me-1" size={14} /> Tout sélectionner</>
                  )}
                </Button>
                <Badge bg="primary" className="p-2">
                  {selectedMarchesForExport.length} marché(s) sélectionné(s)
                </Badge>
              </div>
              <span className="text-muted small">
                {filteredMarchesForExport.length} marché(s) disponible(s)
              </span>
            </div>
            
            {/* Liste des marchés */}
            <div 
              className="border rounded-3"
              style={{ maxHeight: '350px', overflowY: 'auto' }}
            >
              {filteredMarchesForExport.length === 0 ? (
                <div className="p-4 text-center text-muted">
                  <Shop size={48} className="opacity-25 mb-2" />
                  <p className="mb-0">Aucun marché trouvé</p>
                </div>
              ) : (
                filteredMarchesForExport.map(marche => {
                  const isSelected = selectedMarchesForExport.includes(marche.id);
                  return (
                    <div
                      key={marche.id}
                      className={`p-3 border-bottom d-flex align-items-center ${isSelected ? 'bg-success bg-opacity-10' : ''}`}
                      style={{ cursor: 'pointer', transition: 'background 0.2s' }}
                      onClick={() => toggleMarcheSelection(marche.id)}
                    >
                      <Form.Check
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleMarcheSelection(marche.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="me-3"
                      />
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center">
                          <Shop size={16} className="text-primary me-2" />
                          <strong>{marche.nom_marche}</strong>
                          {marche.code_marche && (
                            <Badge bg="secondary" className="ms-2">
                              {marche.code_marche}
                            </Badge>
                          )}
                        </div>
                        <div className="small text-muted mt-1">
                          <GeoAlt size={12} className="me-1" />
                          {getQuartierName(marche.id_quartier)}
                          {marche.etat && (
                            <Badge 
                              bg={marche.etat === "Ouvert" ? "success" : "danger"} 
                              className="ms-2"
                              style={{ fontSize: '10px' }}
                            >
                              {marche.etat}
                            </Badge>
                          )}
                        </div>
                      </div>
                      {isSelected && (
                        <CheckCircle size={20} className="text-success" />
                      )}
                    </div>
                  );
                })
              )}
            </div>
            
            {/* Résumé */}
            {selectedMarchesForExport.length > 0 && (
              <div className="mt-4 p-3 bg-light rounded-3">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div>
                    <strong>Résumé de l'export :</strong>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <Badge bg="success" className="p-2">
                      <CheckCircle className="me-1" size={12} />
                      {selectedMarchesForExport.length} marché(s) sélectionné(s)
                    </Badge>
                  </div>
                </div>
                <div className="mt-2 small text-muted">
                  <strong>Nom du fichier :</strong> Contribuables_
                  {selectedMarchesForExport.length === 1 
                    ? sanitizeFileName(marches.find(m => m.id === selectedMarchesForExport[0])?.nom_marche || '')
                    : selectedMarchesForExport.length <= 3
                      ? selectedMarchesForExport
                          .map(id => sanitizeFileName(marches.find(m => m.id === id)?.nom_marche || ''))
                          .join('_')
                      : `${selectedMarchesForExport
                          .slice(0, 2)
                          .map(id => sanitizeFileName(marches.find(m => m.id === id)?.nom_marche || ''))
                          .join('_')}_et_${selectedMarchesForExport.length - 2}_autres`
                  }_{new Date().toISOString().split('T')[0]}.xlsx
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0 bg-light">
            <Button 
              variant="light" 
              onClick={() => {
                setShowExportModal(false);
                setSelectedMarchesForExport([]);
                setExportSearchTerm('');
              }}
              className="rounded-3"
              disabled={exportLoading}
            >
              Annuler
            </Button>
            <Button 
              variant="success" 
              onClick={exportSelectedMarches}
              disabled={exportLoading || selectedMarchesForExport.length === 0}
              className="rounded-3"
            >
              {exportLoading ? (
                <><Spinner size="sm" className="me-2" /> Export en cours...</>
              ) : (
                <><FileExcel className="me-2" size={16} /> Exporter la sélection ({selectedMarchesForExport.length})</>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        <style>{`
          .stats-card {
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            border-radius: 12px;
            overflow: hidden;
          }
          .stats-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1) !important;
          }
          .card-gradient {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 4px;
            opacity: 0.8;
          }
          .hover-card {
            transition: transform 0.3s ease, box-shadow 0.3s ease;
          }
          .hover-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
          }
          .hover-bg-light:hover {
            background-color: #f8f9fa !important;
          }
          .cursor-pointer {
            cursor: pointer;
          }
          .rounded-circle {
            border-radius: 50% !important;
          }
        `}</style>
        <Footer />
      </div>
    </React.Fragment>
  );
}